import { get, set } from 'lodash';
import { zonedTimeToUtc } from 'date-fns-tz';
import { TimeZone, timeZoneMapping } from './timeZone/enum';
import { DateTime } from 'luxon';
import { validateDate } from './dateFormat/formatDateTime';

const luxonDateToUtc = (dateTime: DateTime) => {
  // We have issues with locale timeZone, so we make sure it is UTC
  let isoString = dateTime.toISO();
  if (isoString) {
    isoString = isoString.replace(/([+-]\d{2}:\d{2})$/, '+00:00');
    dateTime = DateTime.fromISO(isoString);
  }

  return dateTime;
};

function parseHumanDateString(dateString: string, timeZone?: string) {
  if (!dateString) {
    return null;
  }

  // Check if the timezone is 'UTC' and remove it
  if (dateString.endsWith(' UTC')) {
    dateString = dateString.replace(' UTC', '');
  }

  for (const [key, value] of Object.entries(timeZoneMapping)) {
    if (dateString.includes(key)) {
      dateString = dateString.replace(key, value);
      break;
    }
  }

  // First try to parse as simple ISO date format (YYYY-MM-DD)
  try {
    // Check for simple date format like "2022-07-01"
    const simpleDateRegex = /^\d{4}-\d{2}-\d{2}$/;
    if (simpleDateRegex.test(dateString)) {
      const dateTime = DateTime.fromISO(dateString);
      if (dateTime.isValid) {
        return dateTime.toJSDate();
      }
    }

    // Try to parse the date string with seconds
    let dateTime = DateTime.fromFormat(dateString, 'dd MMM yyyy HH:mm:ss z', {
      setZone: true,
    });

    if (!dateTime.isValid) {
      // If the first format fails, try without seconds
      dateTime = DateTime.fromFormat(dateString, 'dd MMM yyyy HH:mm z', {
        setZone: true,
      });
    }

    if (!dateTime.isValid) {
      if (timeZone) {
        dateString = `${dateString} ${timeZone}`;
        dateTime = DateTime.fromFormat(dateString, 'dd MMM yyyy HH:mm:ss z', {
          setZone: true,
        });
      } else {
        dateTime = DateTime.fromFormat(dateString, 'dd MMM yyyy HH:mm:ss');
        dateTime = luxonDateToUtc(dateTime);
      }
    }

    if (!dateTime.isValid) {
      if (timeZone) {
        dateString = `${dateString} ${timeZone}`;
        dateTime = DateTime.fromFormat(dateString, 'dd MMM yyyy HH:mm z', {
          setZone: true,
        });
      } else {
        // If the first format fails, try without seconds
        dateTime = DateTime.fromFormat(dateString, 'dd MMM yyyy HH:mm');
        dateTime = luxonDateToUtc(dateTime);
      }
    }

    // Check if dateTime is valid before converting to native JavaScript Date object
    if (dateTime.isValid) {
      const jsDate = dateTime.toJSDate();
      return jsDate;
    } else {
      throw new Error(`Invalid date format: ${dateTime.toString()}`);
    }
  } catch (error) {
    console.error('Error parsing date string:', error, dateString);
    return null;
  }
}

function isISODateString(dateString: string) {
  const isoRegex =
    /^\d{4}-\d{2}-\d{2}(?:[T\s]\d{2}:\d{2}:\d{2}(?:\.\d+)?(?:Z|[+-]\d{2}:\d{2})?)?$/;
  return isoRegex.test(dateString);
}

export function parseDate(dateString: string | number, timeZone?: string) {
  let date;

  if (typeof dateString === 'number' || /^\d+$/.test(dateString)) {
    let timestamp = parseInt(dateString.toString(), 10);
    if (dateString.toString().length === 10) {
      timestamp *= 1000; // Convert seconds to milliseconds if it's a 10-digit number
    }
    date = new Date(timestamp);
  }
  // Check for ISO 8601 format
  else if (isISODateString(dateString)) {
    if (/^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
      date = new Date(`${dateString}T00:00:00Z`);
    } else if (dateString.endsWith('Z')) {
      // IMPORTANT: When a date string already has the 'Z' indicator (e.g., "2025-05-02T11:50:39.654210Z"),
      // it explicitly indicates that the time is already in UTC/Zulu time
      // Using zonedTimeToUtc() on a Z-terminated string can cause incorrect double-conversion
      // Instead, we use the native Date constructor which correctly interprets the Z indicator
      date = new Date(dateString);
    } else {
      // IMPORTANT: For ISO strings without timezone information (e.g., "2025-05-02T11:50:39.654210"),
      // we need to explicitly specify that they should be interpreted as UTC
      // Without this, JavaScript would interpret the string as a local time, leading to incorrect timestamps
      // zonedTimeToUtc() correctly converts from the specified timezone (UTC) to UTC
      date = zonedTimeToUtc(dateString, TimeZone.UTC);
    }
  }
  // Check for human-readable format
  else {
    date = parseHumanDateString(dateString, timeZone);
  }

  // Validate date object
  if (!validateDate(date)) {
    return null;
  }

  return date;
}

export const convertFieldsToDates = (
  input: Record<string, any> | Record<string, any>[],
  fields: string[],
  timeZone?: string // Only used when there's no timestamp on the date string, but it actually does have a timezone
) => {
  const processField = (obj: Record<string, any>, fieldParts: any) => {
    if (fieldParts.length === 0) return;

    const currentPart = fieldParts.shift();
    if (!currentPart) return;

    const value = get(obj, currentPart);

    if (Array.isArray(value)) {
      value.forEach((item, index) => {
        if (typeof item === 'object') {
          processField(item, [...fieldParts]);
        } else {
          const arrayField = `${currentPart}[${index}]`;
          if (fieldParts.length === 0) {
            const arrayValue = get(obj, arrayField);
            if (arrayValue) {
              set(obj, arrayField, parseDate(arrayValue, timeZone));
            }
          }
        }
      });
    } else if (typeof value === 'object' && value !== null) {
      processField(value, [...fieldParts]);
    } else if (fieldParts.length === 0 && currentPart in obj) {
      set(obj, currentPart, parseDate(value, timeZone));
    }
  };

  // Used when the input is an array of objects, then we run the function recursively
  if (Array.isArray(input)) {
    input.forEach((object) => convertFieldsToDates(object, fields));
    return;
  }

  const object = input;

  fields.forEach((field) => {
    const fieldParts = field.split('.');
    processField(object, fieldParts);
  });
};
